import { useContext, useState } from 'react';
import { Grid } from '@mui/material';
import {
	Button,
	DataTable,
	ModalConfirmation,
	Typography,
} from 'design_system/src';
import { ReactComponent as Reload } from 'design_system/src/static/icons/reload.svg';
import { ReactComponent as LinkRotate } from 'design_system/src/static/icons/link-rotate.svg';

import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { momentLocal } from 'models/shared/helpers/dates';
import {
	retrieveIncodeResults,
	createIncodeSession,
} from 'models/incode/controllers/incode.controller';
import { IncodeScoreValue } from 'models/incode/interfaces/incode.interface';
import {
	IncodeStatusEnum,
	RESULT_STATUS_COLOR,
	RESULT_STATUS_LABEL,
	STATUS_LABEL,
	STATUS_COLOR,
} from 'models/incode/enums/incode.enums';
import { FormStatus } from 'models/financial-form/enums/financial-form.enums';
import { ozonerContext } from '../OzonerContext';

function InfoCard({
	title,
	children,
	action,
	label,
}: {
	title: string;
	children: React.ReactNode;
	action?: React.ReactNode;
	label?: React.ReactNode;
}) {
	return (
		<div className="bg_neutral_0 border_neutral_400 border_solid border_1 br_sm overflow_hidden display_flex flex_col">
			<div className="p_md border_b_2 border_b_solid border_neutral_300 display_flex flex_justify_between">
				<Typography scale="medium" weight="600" textColor="neutral_900">
					{title}
				</Typography>
				{label}
			</div>
			<div className="p_md flex_grow_1">{children}</div>
			{action && (
				<div className="p_md border_t_1 border_t_solid border_neutral_300">
					{action}
				</div>
			)}
		</div>
	);
}

function StatusBadge({ status }: { status: IncodeStatusEnum }) {
	return (
		<Typography
			scale="small"
			weight="600"
			textColor="neutral_0"
			className={`br_xs p_x_md p_y_xxxs bg_${RESULT_STATUS_COLOR[status]}_nocontrast`}
		>
			{RESULT_STATUS_LABEL[status]}
		</Typography>
	);
}

const scoreRow = (label: string, area?: IncodeScoreValue) =>
	area
		? {
				label,
				value: area.value || RESULT_STATUS_LABEL[area.status],
				valueColor: RESULT_STATUS_COLOR[area.status],
		  }
		: null;

function KycOzoner() {
	const { financialForm, updateFormData } = useContext(ozonerContext);
	const [loading, setLoading] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [openConfirmSession, setOpenConfirmSession] = useState(false);
	const [openConfirmResults, setOpenConfirmResults] = useState(false);

	const incode = financialForm?.form?.incode;
	const isRejected =
		financialForm?.form?.status === FormStatus.rejected ||
		financialForm?.form?.status === FormStatus.rejectedKyc;

	const handleCreateSession = () => {
		if (!financialForm?.form?._id || isRejected) return;
		setGenerating(true);
		createIncodeSession(financialForm.form._id)
			.then((response) => {
				navigator.clipboard.writeText(response.onboardingUrl).catch(() => {});
				toasterNotification({
					msg: 'Nueva sesión de Incode generada. Link copiado al portapapeles.',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				updateFormData({
					...financialForm.form,
					incodeHistory: incode
						? [...(financialForm.form.incodeHistory ?? []), incode]
						: financialForm.form.incodeHistory,
					incode: {
						interviewId: response.interviewId,
						onboardingUrl: response.onboardingUrl,
						status: response.status,
						createdAt: new Date().toISOString(),
					},
				});
				setOpenConfirmSession(false);
			})
			.catch(commonErrorHandler)
			.finally(() => setGenerating(false));
	};

	const newSessionButton = (
		<Button
			scale="small"
			color="secondary"
			disabled={isRejected}
			icon={<LinkRotate />}
			onClick={() => {
				if (isRejected) return;
				setOpenConfirmSession(true);
			}}
			className="w_100_per text_neutral_0"
		>
			Generar nueva sesión
		</Button>
	);

	const confirmSessionModal = openConfirmSession && (
		<ModalConfirmation
			openConfirmation={openConfirmSession}
			handleClose={() => setOpenConfirmSession(false)}
			icon={<LinkRotate className="text_primary_300" />}
			widthDesktop={35}
			isConfirmationLoading={generating}
			actions={[
				{
					label: 'Cancelar',
					color: 'grey',
					action: () => setOpenConfirmSession(false),
					disabled: generating,
				},
				{
					label: 'Generar',
					color: 'primary',
					action: handleCreateSession,
					disabled: generating || isRejected,
					loading: generating,
				},
			]}
		>
			<>
				<Typography scale="large" weight="600" className="m_b_xs">
					Generar nueva sesión de Incode
				</Typography>
				<Typography
					scale="medium"
					weight="400"
					className="m_b_md"
					textColor="neutral_700"
				>
					Se creará una nueva sesión de verificación de identidad. El link
					generado debe compartirse con el cliente con prontitud, ya que queda
					asociado a esta sesión específica.
				</Typography>
			</>
		</ModalConfirmation>
	);

	if (!incode) {
		return (
			<div className="flex_center flex_col flex_gap_md p_xl">
				<Typography scale="medium" weight="400" textColor="neutral_600">
					No hay información de KYC (Incode) disponible.
				</Typography>
				<div className="w_30_per">{newSessionButton}</div>
				{confirmSessionModal}
			</div>
		);
	}

	const handleRetrieveResults = () => {
		if (!financialForm?.form?._id) return;
		setLoading(true);
		retrieveIncodeResults(financialForm.form._id)
			.then((response) => {
				toasterNotification({
					msg: 'Resultados de Incode obtenidos',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				updateFormData({
					...financialForm.form,
					incode: {
						...incode,
						status: response.status,
						completedAt: response.completedAt,
						result: response.result,
					},
				});
			})
			.catch(commonErrorHandler)
			.finally(() => setLoading(false));
	};

	const handleConfirmRetrieveResults = () => {
		setOpenConfirmResults(false);
		handleRetrieveResults();
	};

	const { result } = incode;

	const sessionData = [
		{ label: 'Interview ID', value: incode.interviewId },
		{
			label: 'URL',
			value: incode.onboardingUrl ?? '-',
		},
		{
			label: 'Estado',
			value: STATUS_LABEL[incode.status],
			valueColor: STATUS_COLOR[incode.status],
		},
		{
			label: 'Creado',
			value: momentLocal(incode.createdAt).format('DD/MM/YYYY HH:mm'),
		},
		{
			label: 'Completado',
			value: incode.completedAt
				? momentLocal(incode.completedAt).format('DD/MM/YYYY HH:mm')
				: '-',
		},
	];

	const idValidationData = result
		? [
				scoreRow('Validación INE', result.idValidation.idValidation),
				scoreRow('Reconocimiento facial', result.idValidation.faceRecognition),
				scoreRow('Prueba de vida', result.idValidation.liveness),
				scoreRow('Confianza OCR', result.idValidation.idOcrConfidence),
		  ].filter((x): x is NonNullable<typeof x> => x !== null)
		: [];

	const ocrData = result
		? [
				scoreRow('Nombre completo', result.ocrData.fullName),
				scoreRow('Apellido paterno', result.ocrData.lastName),
				scoreRow('Apellido materno', result.ocrData.secondLastName),
				scoreRow('CURP', result.ocrData.curp),
				scoreRow('País', result.ocrData.country),
		  ].filter((x): x is NonNullable<typeof x> => x !== null)
		: [];

	return (
		<Grid container direction="row" spacing={3} className="p_t_md">
			<Grid item xs={5}>
				<InfoCard
					title="Sesión Incode"
					label={
						incode.result?.status ? (
							<StatusBadge status={incode.result?.status} />
						) : undefined
					}
					action={
						<div className="display_flex flex_gap_sm">
							<Button
								scale="small"
								color="primary"
								disabled={loading}
								icon={<Reload />}
								onClick={() => setOpenConfirmResults(true)}
								className="w_100_per"
							>
								{loading ? 'Obteniendo...' : 'Obtener resultados'}
							</Button>
							{newSessionButton}
						</div>
					}
				>
					<DataTable
						data={sessionData}
						weightLabels={2}
						weightValues={2}
						canCopy
					/>
				</InfoCard>
			</Grid>

			<Grid item xs={7}>
				<Grid container direction="row" spacing={3}>
					<Grid item xs={12}>
						{result && idValidationData.length > 0 ? (
							<InfoCard
								title="Validación de identidad"
								label={<StatusBadge status={result.idValidation.status} />}
							>
								<DataTable
									data={idValidationData}
									weightLabels={2}
									weightValues={2}
								/>
							</InfoCard>
						) : (
							<div className="flex_center p_xl bg_neutral_0 border_neutral_400 border_solid border_1 br_sm h_100_per">
								<Typography scale="small" weight="400" textColor="neutral_600">
									Resultados pendientes de obtener.
								</Typography>
							</div>
						)}
					</Grid>

					{result && ocrData.length > 0 && (
						<Grid item xs={12}>
							<InfoCard
								title="Datos OCR (vs. datos del ozoner)"
								label={<StatusBadge status={result.ocrData.status} />}
							>
								<DataTable data={ocrData} weightLabels={2} weightValues={2} />
							</InfoCard>
						</Grid>
					)}

					{result?.watchlist && (
						<Grid item xs={12}>
							<InfoCard
								title="Watchlist"
								label={<StatusBadge status={result.watchlist.status} />}
							>
								<DataTable
									data={[
										{
											label: 'Hits totales',
											value: String(result.watchlist.totalHits),
										},
										{
											label: 'Coincidencias totales',
											value: String(result.watchlist.totalMatches),
										},
										{
											label: 'Exactitud de la búsqueda',
											value: String(result.watchlist.fuzziness),
										},
										{
											label: 'Listas de seguimiento',
											value: result.watchlist.types?.join(', ') || '—',
										},
									]}
									weightLabels={2}
									weightValues={4}
								/>
							</InfoCard>
						</Grid>
					)}
				</Grid>
			</Grid>

			<Grid item xs={12} />
			{confirmSessionModal}
			{openConfirmResults && (
				<ModalConfirmation
					openConfirmation={openConfirmResults}
					handleClose={() => setOpenConfirmResults(false)}
					icon={<Reload className="text_primary_300" />}
					widthDesktop={35}
					isConfirmationLoading={loading}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setOpenConfirmResults(false),
							disabled: loading,
						},
						{
							label: 'Obtener resultados',
							color: 'primary',
							action: handleConfirmRetrieveResults,
							disabled: loading,
							loading,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_xs">
							Obtener resultados de Incode
						</Typography>
						<Typography
							scale="medium"
							weight="400"
							className="m_b_md"
							textColor="neutral_700"
						>
							Si ejecutas esta acción y la validación falla,{' '}
							<b>deberás rechazar manualmente la solicitud</b> — esta acción no
							rechaza la solicitud de forma automática.
						</Typography>
					</>
				</ModalConfirmation>
			)}
		</Grid>
	);
}

export default KycOzoner;
